<template>
    <div @click.stop>
        <link :href="fontUrl" rel="stylesheet" />
        <p class="mb-0 small font-weight-bold form-divider-label text-secondary">Global Theme</p>
        <hr class="mt-1 pt-1 mb-1" />
        <p class="mb-3 small text-secondary">Choose a global theme for your scenario.</p>
        <div class="d-flex form-input-container">
            <div class="d-flex flex-wrap">
                <div
                    v-for="option in themeOptions"
                    :key="option.value"
                    class="rounded-circle color-preview-container flex-grow-0"
                    :class="{ 'color-preview-selected': caseObjClone.global_theme_color == option.value }"
                    role="button"
                    @click="setGlobalColor(option.value)"
                >
                    <div
                        class="rounded-circle color-preview"
                        :style="colorPreviewStyle(option)"
                    ></div>
                </div>
                <div
                    class="rounded-circle color-preview-container"
                    role="button"
                    :class="{ 'color-preview-selected': caseObjClone.global_theme_color == '' }"
                >
                    <div class="rounded-circle color-preview color-preview-default" @click="setGlobalColor('')"></div>
                </div>
            </div>
        </div>
        <p class="mb-0 small font-weight-bold form-divider-label text-secondary pt-4">Font</p>
        <hr class="mt-1 pt-1 mb-1" />
        <p class="mb-3 small text-secondary">Customize your scenario's font.</p>
        <div class="form-input-container">
            <v-select
                v-model="caseObjClone.global_font_family_name"
                :options="fontOptions"
                :close-on-select="true"
                label="label"
                class="mw-100 mb-1"
                placeholder="Select a font"
                :reduce="(option) => option.value"
                :clearable="false"
                @input="updateGlobalStyles()"
            >
                <template slot="option" slot-scope="option">
                    <span :style="{ 'font-family': option.value }">
                        {{ option.label }}
                    </span>
                </template>
                <template v-slot:selected-option="option">
                    <span :style="{ 'font-family': option.value }">
                        {{ option.label }}
                    </span>
                </template>
            </v-select>
        </div>
    </div>
</template>

<script>
import http from "@/axios.js"
import { globalThemeOptions } from "@/assets/js/settings.js"

export default {
    name: "GlobalStyleForm",
    props: {
        value: {
            type: Object,
            required: false,
        },
        autoSave: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            caseObjClone: {},
            fontOptions: [
                {
                    label: "Device Default",
                    value: "",
                },
                {
                    label: "Rubik",
                    value: "Rubik",
                },
                {
                    label: "Onest",
                    value: "Onest",
                },
                {
                    label: "Nunito",
                    value: "Nunito",
                },
                {
                    label: "Ubuntu",
                    value: "Ubuntu",
                },
                {
                    label: "PT Sans",
                    value: "PT Sans Caption",
                },
                {
                    label: "Exo",
                    value: "Exo 2",
                },
                {
                    label: "Work Sans",
                    value: "Work Sans",
                },
            ],
        }
    },
    computed: {
        // A URL to fetch the list of typefaces from Google Fonts, which are used to
        // style each option in the font selector.
        fontUrl() {
            let fontFamilyString = this.fontOptions.map(o => o.value).join("&family=")
            return `https://fonts.googleapis.com/css2?familly=${fontFamilyString}&display=swap`
        },
        themeOptions() {
            let options = []
            for (const [key, value] of Object.entries(globalThemeOptions)) {
                options.push({
                    value: key,
                    ...value
                })
            }
            return options
        },
    },
    watch: {
        value: function (newValue) {
            this.caseObjClone = Object.assign({}, newValue)
        },
    },
    methods: {
        // Set the global color theme.
        setGlobalColor(theme) {
            this.caseObjClone.global_theme_color = theme
            this.updateGlobalStyles()
        },
        updateGlobalStyles() {
            this.$emit('input', this.caseObjClone)
            if (this.autoSave) {
                this.$store.commit("setGlobalCaseStyles", this.caseObjClone)
                // Persist the change in the db.
                http.put(`/api/cases/${this.caseObjClone.id}/theme/`, {
                    global_font_family_name: this.caseObjClone.global_font_family_name,
                    global_theme_color: this.caseObjClone.global_theme_color
                })
            }
        },
        // Returns a css background for each theme option.
        colorPreviewStyle(theme) {
            const gradientValues = `315deg, ${theme.secondary} 50%, ${theme.primary} 50%`
            return {
                "background-image": `linear-gradient(${gradientValues})`,
            }
        },
    },
    mounted() {
        this.caseObjClone = Object.assign({}, this.value)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.color-preview-default {
    background-image: linear-gradient(315deg, $softgreen 50%, #304b6c 50%);
    background-image: -webkit-linear-gradient(315deg, $softgreen 50%, #304b6c 50%);
}

.color-preview-selected {
    border-color: $primary !important;
}

.color-preview-container {
    padding: 2px;
    border: solid 3px transparent;
    margin-right: 2px;
}

.color-preview {
    width: 26px;
    height: 26px;
    cursor: pointer;
}

.form-input-container {
    max-width: 400px;
}
</style>